import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '@/lib/analytics';

const BASE_URL = 'https://www.peoplecarehome.co.uk';

const DEFAULT_IMAGE = `${BASE_URL}/brand/og-image.jpg`;

const HOME = {
  title: 'People Care Home — AI-Powered Care Management Platform | Care Unlimited',
  desc: 'People Care Home is the all-in-one AI-powered care management platform for domiciliary care, care homes, and nursing facilities. CQC compliant. Built in the UK.',
  image: 'https://images.unsplash.com/photo-1762955911769-d652ceaa94bb?w=1200&q=80',
};

const PAGE_META = {
  '/': HOME,
  '/products': {
    title: 'Care Management Software Suite — People Care Home',
    desc: 'Purpose-built software for domiciliary, residential, nursing and enterprise care. Explore the full People Care Home product suite for UK care providers.',
    image: 'https://images.unsplash.com/photo-1762955911235-adeb8838dcf1?w=1200&q=80',
  },
  '/pricing': {
    title: 'Pricing — Flat-Rate UK Care Software | People Care Home',
    desc: 'Simple, transparent pricing for UK care providers. 40–60% cheaper than competitors. Free 1-week trial, no contracts, no hidden fees, no per-user surprises.',
    image: 'https://images.unsplash.com/photo-1758686254601-a47850cb2226?w=1200&q=80',
  },
  '/about': {
    title: 'About — Built by People Who Care | People Care Home',
    desc: 'People Care Home Limited transforms UK care delivery through technology — making life better for residents, carers and families across the country.',
    image: 'https://images.unsplash.com/photo-1762955911769-d652ceaa94bb?w=1200&q=80',
  },
  '/contact': {
    title: 'Contact & Book a Demo — People Care Home',
    desc: 'Book a demo, ask a question, or get in touch with our UK-based care technology team. We are ready to help you every step of the way.',
    image: 'https://images.unsplash.com/photo-1762955911235-adeb8838dcf1?w=1200&q=80',
  },
  '/training': {
    title: 'Training & Resources — People Care Home',
    desc: 'Video tutorials, certification courses and documentation for every role in your care organisation. Secure portal access for verified users.',
    image: 'https://images.unsplash.com/photo-1516841273335-e39b37888115?w=1200&q=80',
  },
  '/domiciliary-care': {
    title: 'Domiciliary Care Software — People Care Home',
    desc: 'Mobile-first domiciliary care platform with GPS visit tracking, eMAR, AI scheduling, CQC compliance and a family portal — built for UK homecare.',
    image: 'https://images.unsplash.com/photo-1543333995-a78aea2eee50?w=1200&q=80',
  },
  '/care-home-management': {
    title: 'Care Home Management Software — People Care Home',
    desc: 'All-in-one care home management: resident records, eMAR, CQC compliance, kitchen, rosters and multi-branch oversight in one platform.',
    image: 'https://images.unsplash.com/photo-1762955911769-d652ceaa94bb?w=1200&q=80',
  },
  '/nursing-home-management': {
    title: 'Nursing Home Management — People Care Home',
    desc: 'Clinical-grade tools for nursing homes: eMAR, wound care tracking, clinical handovers and GP integration. NEWS2 monitoring coming soon.',
    image: 'https://images.unsplash.com/photo-1773227054058-afd18b554e8f?w=1200&q=80',
  },
  '/enterprise-solutions': {
    title: 'Enterprise Care Solutions — People Care Home',
    desc: 'Centralised group dashboards, SSO, cross-site analytics and dedicated support for care groups managing 10 to 1,000+ homes.',
  },
  '/ai-features': {
    title: 'AI Features — Predictive Care Intelligence | People Care Home',
    desc: 'AI risk alerts, care quality optimisation, voice-to-text notes and compliance intelligence built directly into the care platform.',
    image: 'https://images.unsplash.com/photo-1587556930799-8dca6fad6d41?w=1200&q=80',
  },
  '/data-migration': {
    title: 'Data Migration — Zero-Downtime Switch | People Care Home',
    desc: 'Expert migration from Nourish, CareDocs, PCS and more. 100% data integrity, zero downtime and a 30-day hypercare period.',
  },
  '/customer-support': {
    title: 'Customer Support — People Care Home',
    desc: 'UK-based phone, email, ticket and live chat support with a 500+ article knowledge base and sub-2-hour response times.',
  },
  '/partner-program': {
    title: 'Partner Program — People Care Home',
    desc: 'Referral, reseller and technology partner programmes. Earn recurring revenue while helping transform UK care delivery.',
  },
  '/case-studies': {
    title: 'Case Studies — Real Results for UK Care Providers | People Care Home',
    desc: 'See how UK care providers improved occupancy, cut admin by up to 70% and achieved Outstanding CQC ratings with People Care Home.',
    image: 'https://images.unsplash.com/photo-1543333995-a78aea2eee50?w=1200&q=80',
  },
  '/training-portal': {
    title: 'Training Portal — People Care Home',
    desc: 'Access certification courses and track your training progress. Secure login for verified People Care Home application users.',
    noindex: true,
  },
  '/manage-training': {
    title: 'Manage Training — People Care Home',
    desc: 'Manage courses, enrolments, compliance and analytics across your organisation with People Care Home Training Management.',
    noindex: true,
  },
  '/training-configuration': {
    title: 'Training Configuration — People Care Home',
    desc: 'Configure training modules, certificates and compliance settings for your organisation.',
    noindex: true,
  },
  '/privacy-policy': {
    title: 'Privacy Policy — People Care Home',
    desc: 'How People Care Home collects, uses and protects your personal data in line with UK GDPR and the Data Protection Act 2018.',
  },
  '/terms-of-service': {
    title: 'Terms of Service — People Care Home',
    desc: 'The terms of service governing use of the People Care Home care management platform.',
  },
  '/cookie-policy': {
    title: 'Cookie Policy — People Care Home',
    desc: 'How People Care Home uses cookies and how you can manage your cookie preferences.',
  },
  '/gdpr': {
    title: 'GDPR Compliance — People Care Home',
    desc: 'People Care Home is fully compliant with UK GDPR and the Data Protection Act 2018, with UK-based data hosting.',
  },
  '/data-processing': {
    title: 'Data Processing Agreement — People Care Home',
    desc: 'Our Data Processing Agreement and approved subprocessors for the People Care Home platform.',
  },
  '/careers': {
    title: 'Careers — Build the Future of Care | People Care Home',
    desc: 'Join a mission-driven UK healthtech team building AI-powered care management software. Open roles in engineering, AI, sales, marketing and customer success.',
  },
  '/blog': {
    title: 'Resources & Insights — People Care Home',
    desc: 'Practical guides on AI, CQC compliance, staffing, data migration and family engagement for UK domiciliary, care home and nursing providers.',
    image: 'https://images.unsplash.com/photo-1765896387403-3e6e0e44d7cc?w=1200&q=80',
  },
  '/compare': {
    title: 'Compare Care Software — People Care Home vs Legacy & eMR | People Care Home',
    desc: 'See how People Care Home compares to legacy on-premise systems and single-purpose eMAR tools on features, cost and risk — 40–60% lower cost, all-in-one.',
  },
  '/admin/submissions': {
    title: 'Submissions — People Care Home Admin',
    desc: 'Internal administration.',
    noindex: true,
  },
  '/profile': {
    title: 'Your Profile — People Care Home',
    desc: 'Your CareConnect account details.',
    noindex: true,
  },
  '/email-templates': {
    title: 'Email Templates — People Care Home',
    desc: 'Internal tool for downloading transactional email template HTML.',
    noindex: true,
  },
  '/quote-builder': {
    title: 'Quote Builder — People Care Home',
    desc: 'Internal tool for building custom People Care Home pricing quotes.',
    noindex: true,
  },
};

function upsertMeta(selector, attr, key, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertBreadcrumbJsonLd(pathname, pageTitle, canonical) {
  const id = 'breadcrumb-jsonld';
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  const items = [{ '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL }];
  if (pathname !== '/') {
    // Strip any trailing " — People Care Home" suffix so the crumb reads as a short label.
    const label = pageTitle.split(' — ')[0].split(' | ')[0];
    items.push({ '@type': 'ListItem', position: 2, name: label, item: canonical });
  }
  el.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  });
}

export default function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const isBlogArticle = pathname.indexOf('/blog/') === 0;
    // '/training/:courseId' — gated course content, distinct from the public '/training'
    // marketing page and from '/training-portal' (different prefix).
    const isTrainingCourse = pathname.indexOf('/training/') === 0;
    const meta = isBlogArticle
      ? {
          title: 'Care Insights — People Care Home',
          desc: 'Practical guides and insights for UK care providers on AI, compliance, staffing and technology.',
        }
      : isTrainingCourse
      ? {
          title: 'Course — People Care Home Training',
          desc: 'Secure course content for verified People Care Home application users.',
          noindex: true,
        }
      : PAGE_META[pathname] || HOME;
    // Normalise canonical path (strip trailing slash except root)
    const canonPath = pathname === '/' ? '/' : pathname.replace(/\/$/, '');
    // Use the live origin so the canonical/og:url always resolve (passes Lighthouse's
    // canonical audit and stays correct once deployed to the production domain).
    const origin = typeof window !== 'undefined' ? window.location.origin : BASE_URL;
    const canonical = `${origin}${canonPath}`;

    document.title = meta.title;
    // index.html ships a site-wide `index, follow`. Admin routes must override it —
    // they are gated, so a crawler would only ever index the access-denied screen,
    // and the URL itself is not something to publish.
    upsertMeta(
      'meta[name="robots"]',
      'name',
      'robots',
      meta.noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large'
    );
    upsertMeta('meta[name="googlebot"]', 'name', 'googlebot', meta.noindex ? 'noindex, nofollow' : 'index, follow');
    upsertMeta('meta[name="description"]', 'name', 'description', meta.desc);
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', meta.title);
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', meta.desc);
    upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', meta.image || DEFAULT_IMAGE);
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', meta.title);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', meta.desc);
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', canonical);
    upsertMeta('meta[property="og:image"]', 'property', 'og:image', meta.image || DEFAULT_IMAGE);
    upsertMeta('meta[property="og:type"]', 'property', 'og:type', isBlogArticle ? 'article' : 'website');
    upsertCanonical(canonical);
    upsertBreadcrumbJsonLd(pathname, meta.title, canonical);
    trackPageView(canonPath, meta.title);
  }, [pathname]);

  return null;
}